import React from 'react';
import { CheckCircle2, FileUp, Info, Pencil, Trash2, Wrench } from 'lucide-react';
import { useHorizontalWheelScroll } from '../../../hooks/useHorizontalWheelScroll';
import { SETTINGS_BTN_DANGER_OUTLINE } from '../../settings/utils/settingsSectionStyles';
import type { FuhrparkDetailTab } from '../../../utils/fuhrparkRoutes';
import type { VehicleDetail, VehicleFuelTransaction } from '../types';
import { formatKm, formatMonthYear } from '../format';
import { vehiclePhotoUrl } from '../api';
import { ReminderStateBadge, VehicleStatusBadge } from './FuhrparkStatusBadge';
import { DocumentsTab, MaintenanceTab, RepairsTab } from './FuhrparkDetailTables';
import { FuelTab } from './FuhrparkFuelTab';
import { DriverAvatar, EmptyState, FUHRPARK_BTN_GHOST, FUHRPARK_BTN_GREEN } from './fuhrparkUi';

const TABS: Array<{ key: FuhrparkDetailTab; label: string; mobileLabel: string }> = [
  { key: 'overview', label: 'Übersicht', mobileLabel: 'Übersicht' },
  { key: 'maintenance', label: 'Wartung & Erinnerungen', mobileLabel: 'Wartung' },
  { key: 'docs', label: 'Verträge & Dokumente', mobileLabel: 'Dokumente' },
  { key: 'repairs', label: 'Reparaturen', mobileLabel: 'Reparaturen' },
  { key: 'fuel', label: 'Tankkarte & Tankungen', mobileLabel: 'Tanken' },
];

const SECTION_LABEL = 'text-[11px] font-semibold text-app-text-muted';
const KPI_LABEL = 'text-xs font-medium text-app-text-subtle';

export function FuhrparkDetailView({
  detail,
  tab,
  canEdit,
  onBack,
  onTab,
  onEdit,
  onUpload,
  onPhoto,
  onToggleInactive,
  onSetStatus,
  onAddReminder,
  onEditReminder,
  onCompleteReminder,
  onReopenReminder,
  onDeleteReminder,
  onAddRepair,
  onEditRepair,
  onDeleteRepair,
  onDeleteVehicle,
  onDeleteDoc,
  onDeletePhoto,
  onPreviewDoc,
  onAddFuelTx,
  onEditFuelTx,
  onDeleteFuelTx,
  onEditFuelCard,
  onDeleteFuelCard,
}: {
  detail: VehicleDetail;
  tab: FuhrparkDetailTab;
  canEdit: boolean;
  onBack: () => void;
  onTab: (tab: FuhrparkDetailTab) => void;
  onEdit: () => void;
  onUpload: () => void;
  onPhoto: (file: File) => void;
  onToggleInactive: () => void;
  onSetStatus: (status: 'workshop' | 'active') => void;
  onAddReminder: () => void;
  onEditReminder: (id: string) => void;
  onCompleteReminder: (id: string) => void;
  onReopenReminder: (id: string) => void;
  onDeleteReminder: (id: string) => void;
  onAddRepair: () => void;
  onEditRepair: (id: string) => void;
  onDeleteRepair: (id: string) => void;
  onDeleteVehicle: () => void;
  onDeleteDoc: (id: string) => void;
  onDeletePhoto: () => void;
  onPreviewDoc: (id: string) => void;
  onAddFuelTx: () => void;
  onEditFuelTx: (tx: VehicleFuelTransaction) => void;
  onDeleteFuelTx: (id: string) => void;
  onEditFuelCard: () => void;
  onDeleteFuelCard: () => void;
}) {
  const v = detail.vehicle;
  const photoInput = React.useRef<HTMLInputElement | null>(null);
  const setTabScrollNode = useHorizontalWheelScroll<HTMLDivElement>();
  const huDue =
    v.hu_due_on ||
    detail.reminders.find((r) => r.reminder_type === 'hu_au' && r.due_state !== 'done')?.due_on ||
    null;
  const openReminders = detail.reminders.filter((r) => r.due_state !== 'done');

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-app-surface lg:overflow-hidden">
      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-app-border px-4 py-3.5 md:px-5">
        <button
          type="button"
          onClick={onBack}
          className="grid h-8 w-8 shrink-0 place-items-center rounded text-app-text-muted hover:bg-app-hover hover:text-app-text"
          aria-label="Zurück zur Übersicht"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="flex flex-wrap items-baseline gap-x-2.5 text-lg font-semibold text-app-text">
              <span>{v.plate || v.model || 'Fahrzeug'}</span>
              {v.plate && v.model ? <span className="font-normal text-app-text-muted">{v.model}</span> : null}
            </h1>
            <VehicleStatusBadge status={v.status} hasDue={v.has_due} className="ml-1" />
          </div>
          <p className="mt-0.5 text-xs text-app-text-subtle">
            {[v.brand || null, v.year ? `Baujahr ${v.year}` : null, v.mileage > 0 ? formatKm(v.mileage) : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        {canEdit ? (
          <div className="grid basis-full grid-cols-2 gap-2 md:flex md:basis-auto md:flex-wrap md:items-center">
            {v.status !== 'workshop' ? (
              <button type="button" className={`${FUHRPARK_BTN_GHOST} w-full md:w-auto`} onClick={() => onSetStatus('workshop')}>
                <Wrench className="h-3.5 w-3.5" />
                In Werkstatt
              </button>
            ) : (
              <button type="button" className={`${FUHRPARK_BTN_GHOST} w-full md:w-auto`} onClick={() => onSetStatus('active')}>
                <CheckCircle2 className="h-3.5 w-3.5" />
                Aktiv
              </button>
            )}
            <button type="button" className={`${FUHRPARK_BTN_GHOST} w-full md:w-auto`} onClick={onToggleInactive}>
              <Info className="h-3.5 w-3.5" />
              {v.status === 'inactive' ? 'Aktivieren' : 'Auf inaktiv stellen'}
            </button>
            <button type="button" className={`${FUHRPARK_BTN_GHOST} w-full md:w-auto`} onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
              Bearbeiten
            </button>
            <button type="button" className={`${FUHRPARK_BTN_GREEN} w-full md:w-auto`} onClick={onUpload}>
              <FileUp className="h-3.5 w-3.5" />
              Dokument
            </button>
          </div>
        ) : null}
      </header>

      <div className="flex flex-none flex-col overflow-visible lg:min-h-0 lg:flex-1 lg:flex-row lg:overflow-hidden">
        <aside className="flex shrink-0 flex-col overflow-visible border-b border-app-border bg-app-surface-muted p-[18px] lg:h-full lg:w-[248px] lg:overflow-y-auto lg:border-b-0 lg:border-r">
          <div className="group relative mb-4">
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => photoInput.current?.click()}
              onDragOver={(e) => {
                if (!canEdit) return;
                e.preventDefault();
              }}
              onDrop={(e) => {
                if (!canEdit) return;
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (file) onPhoto(file);
              }}
              className="block h-[150px] w-full overflow-hidden rounded-[10px] bg-app-surface"
            >
              {v.has_photo ? (
                <img src={vehiclePhotoUrl(v.id)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center border border-dashed border-app-border px-3 text-center text-sm text-app-text-subtle">
                  Fahrzeugfoto hierher ziehen
                </div>
              )}
            </button>
            {canEdit && v.has_photo ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePhoto();
                }}
                className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/90 text-app-text-muted shadow-sm opacity-0 transition-opacity hover:bg-white hover:text-rose-600 focus-visible:opacity-100 group-hover:opacity-100"
                aria-label="Foto entfernen"
                title="Foto entfernen"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <input
            ref={photoInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onPhoto(file);
              e.target.value = '';
            }}
          />

          <h2 className={`${SECTION_LABEL} mb-3`}>Fahrzeugdaten</h2>
          <dl>
            <SpecRow label="Marke" value={v.brand} />
            <SpecRow label="Modell" value={v.model} />
            <SpecRow label="Baujahr" value={v.year != null ? String(v.year) : ''} />
            <SpecRow label="Erstzulassung" value={v.first_registration ? formatMonthYear(v.first_registration) : ''} />
            <SpecRow label="Nächste HU/AU" value={huDue ? formatMonthYear(huDue) : ''} />
            <SpecRow label="Kilometerstand" value={v.mileage > 0 ? formatKm(v.mileage) : ''} />
            <SpecRow label="FIN" value={v.vin} />
          </dl>

          <h2 className={`${SECTION_LABEL} mb-3 mt-5`}>Zugewiesener Fahrer</h2>
          <div
            role={canEdit ? 'button' : undefined}
            tabIndex={canEdit ? 0 : undefined}
            onClick={canEdit ? onEdit : undefined}
            onKeyDown={(e) => {
              if (!canEdit) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEdit();
              }
            }}
            className={`group relative flex items-center gap-2.5 rounded-lg border border-app-border bg-app-surface p-2.5 ${
              canEdit ? 'cursor-pointer hover:border-app-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30' : ''
            }`}
            title={canEdit ? 'Fahrer ändern' : undefined}
          >
            {v.driver ? (
              <>
                <DriverAvatar driver={v.driver} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-app-text">{v.driver.name}</p>
                  {v.driver.position ? <p className="truncate text-[11px] text-app-text-subtle">{v.driver.position}</p> : null}
                </div>
              </>
            ) : (
              <p className="flex-1 text-[13px] text-app-text-subtle">Nicht zugewiesen</p>
            )}
            {canEdit ? (
              <Pencil
                className="h-3.5 w-3.5 shrink-0 text-app-text-subtle opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                aria-hidden
              />
            ) : null}
          </div>
          {canEdit ? (
            <button type="button" className={`${SETTINGS_BTN_DANGER_OUTLINE} mt-auto w-full text-xs`} onClick={onDeleteVehicle}>
              Fahrzeug löschen
            </button>
          ) : null}
        </aside>

        <section className="flex min-w-0 flex-none flex-col overflow-visible lg:min-h-0 lg:flex-1 lg:overflow-hidden">
          <div
            ref={setTabScrollNode}
            className="flex shrink-0 overflow-x-auto border-b border-app-border px-4 md:px-5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => onTab(t.key)}
                  className={`relative h-9 shrink-0 whitespace-nowrap px-3.5 text-sm font-medium transition-colors ${
                    active
                      ? 'text-pipedrive-blue after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-pipedrive-blue'
                      : 'text-app-text-muted hover:text-app-text'
                  }`}
                >
                  <span className="md:hidden">{t.mobileLabel}</span>
                  <span className="hidden md:inline">{t.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-4 md:p-5 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
            {tab === 'overview' ? (
              <div>
                <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <OverviewKpi label="Kilometerstand" value={formatKm(v.mileage)} />
                  <OverviewKpi label="Nächste HU/AU" value={huDue ? formatMonthYear(huDue) : '—'} />
                </div>
                <h2 className="mb-2.5 text-[13px] font-semibold text-app-text">Anstehende Wartungen & Erinnerungen</h2>
                <ReminderRows
                  reminders={openReminders}
                  empty="Keine offen"
                  canEdit={canEdit}
                  onEdit={onEditReminder}
                />
              </div>
            ) : null}

            {tab === 'maintenance' ? (
              <MaintenanceTab
                reminders={detail.reminders}
                serviceEvents={detail.service_events ?? []}
                canEdit={canEdit}
                onAdd={onAddReminder}
                onEdit={onEditReminder}
                onComplete={onCompleteReminder}
                onReopen={onReopenReminder}
                onDelete={onDeleteReminder}
                onPreviewDoc={onPreviewDoc}
              />
            ) : null}

            {tab === 'docs' ? (
              <DocumentsTab
                documents={detail.documents}
                canEdit={canEdit}
                onUpload={onUpload}
                onPreview={onPreviewDoc}
                onDelete={onDeleteDoc}
              />
            ) : null}

            {tab === 'repairs' ? (
              <RepairsTab
                repairs={detail.repairs}
                canEdit={canEdit}
                onAdd={onAddRepair}
                onEdit={onEditRepair}
                onDelete={onDeleteRepair}
              />
            ) : null}

            {tab === 'fuel' ? (
              <FuelTab
                fuel={detail.fuel}
                canEdit={canEdit}
                onAddTx={onAddFuelTx}
                onEditTx={onEditFuelTx}
                onDeleteTx={onDeleteFuelTx}
                onEditCard={onEditFuelCard}
                onDeleteCard={onDeleteFuelCard}
              />
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

function OverviewKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-app-border bg-app-surface p-3.5">
      <p className={KPI_LABEL}>{label}</p>
      <p className="mt-1.5 text-xl font-semibold text-app-text">{value}</p>
    </div>
  );
}

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 border-b border-app-border py-[7px] text-xs">
      <dt className="text-app-text-subtle">{label}</dt>
      <dd className="text-right font-medium break-words text-app-text-muted">{value || 'Keine Angabe'}</dd>
    </div>
  );
}

function reminderSub(r: VehicleDetail['reminders'][number]): string {
  const parts = [`Zuletzt: ${formatMonthYear(r.last_done_on)}`];
  if (r.interval_note) parts.push(r.interval_note);
  if (r.due_mileage != null) parts.push(`oder bei ${r.due_mileage.toLocaleString('de-DE')} km`);
  return parts.join(' · ');
}

function ReminderRows({
  reminders,
  empty,
  canEdit,
  onEdit,
}: {
  reminders: VehicleDetail['reminders'];
  empty: string;
  canEdit?: boolean;
  onEdit?: (id: string) => void;
}) {
  if (!reminders.length) return <EmptyState text={empty} />;
  return (
    <div className="overflow-hidden rounded-lg border border-app-border bg-app-surface">
      {reminders.map((r) => (
        <article
          key={r.id}
          className={`flex items-center gap-3 border-b border-app-border px-3.5 py-3 last:border-b-0 ${
            canEdit ? 'cursor-pointer hover:bg-app-surface-muted' : ''
          }`}
          onClick={() => canEdit && onEdit?.(r.id)}
        >
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-app-text">{r.display_label}</p>
            <p className="text-xs text-app-text-subtle">{reminderSub(r)}</p>
          </div>
          <ReminderStateBadge state={r.due_state} />
          <p className="shrink-0 text-xs text-app-text-muted">Fällig: {formatMonthYear(r.due_on)}</p>
        </article>
      ))}
    </div>
  );
}
